import { config } from './config.js';

// Genotype Carrier-Status annotation — the +/- glyph hung off a node's top-right
// corner. Pure + d3-FREE: the geometry lives here so the renderer AND the other
// tenants of that corner (the proxy "P", the nomenclature person number) place
// themselves from ONE set of numbers and cannot drift apart.
//
// NOTE: this is the legacy per-genotype annotation. It is NOT the gene→disease
// carrier marks in ../carrier-status/ (bold perimeter / Bennett vertical line) —
// those are a separate, deliberately-untouched pipeline (see Canvas).

// carrierStatus → glyph.
//
// TEST-RESULT notation: `+` means the variant was FOUND, `-` means it was not. Same
// direction as NSGC E notation (`E2+ (5385insC BRCA1)`), and what the customer
// specified on 19 June 2026 — heterozygote, homozygote and hemizygote all indicate a
// carrier (+); normal indicates a non-carrier (-).
//
// DO NOT reintroduce the paired glyphs this replaced (`+/+`, `+/-`, `-/-`). Those were
// ALLELE notation, in which `+` denotes the WILD-TYPE allele — the opposite meaning
// for the same character. This marker previously carried both systems at once, so a
// homozygous-normal patient rendered `-/-` and read as homozygous mutant to anyone
// using allele notation. Mixing the two is a patient-safety risk (TGCLOUD-1647).
//
// Zygosity is not lost: it is still carried by the genotype record and its pedigree
// label. It is deliberately no longer encoded in the glyph.
//
// NB the corner marker itself is a TrakGene product convention agreed with the
// customer, NOT part of the NSGC standard — which annotates test results as E-notation
// text beneath the symbol. It is intentionally not aligned to the standard.
// Grouped to mirror CONSTANTS.carrierStatuses: everything deriving '+', then '-'.
export const CARRIER_STATUS_SYMBOL = {
  carrier: '+',
  heterozygote: '+',
  homozygote: '+',
  hemizygote: '+',
  'homozygote-or-compound-heterozygote': '+',
  'non-carrier': '-',
  normal: '-',
  'homozygous-normal': '-'
};

// Statuses meaning "this person carries it". Drives the aggregation rule below: a
// carrier-positive status wins over a non-carrier one (customer, 1 July 2026).
//
// Deliberately NOT the same set as TESTED_CARRIER_VALUES in ../carrier-status/ — that
// one decides whether to draw a gene→disease CARRIER MARK, a clinical statement about
// the condition, and excludes the affected-level statuses. This set only decides which
// of a person's several test results the single marker should report.
const POSITIVE_STATUSES = new Set([
  'carrier',
  'heterozygote',
  'homozygote',
  'hemizygote',
  'homozygote-or-compound-heterozygote'
]);

/**
 * The single mark a node shows, or '' when it has none. Exactly ONE mark renders
 * per node, so when several genotypes carry a status a carrier-positive one wins
 * (a carrier is always flagged); otherwise the first INTERPRETABLE status shows.
 *
 * Only a status that derives a glyph can represent the node. An unrecognised value
 * must never SUPPRESS an interpretable result behind it: the fallback used to take
 * the first genotype with any status at all, so a person recording
 * ['anything', 'non-carrier'] showed NO mark while ['non-carrier', 'anything'] showed
 * '-' — the same clinical data drawn differently according to array order, with a
 * real non-carrier result silently dropped (TGCLOUD-1647).
 *
 * @param {{ genotypes?: Array<{ carrierStatus?: string }> }} d node datum
 * @returns {string}
 */
export function carrierStatusMarkText(d) {
  const derivable = (Array.isArray(d?.genotypes) ? d.genotypes : []).filter(
    (g) => g && g.carrierStatus && CARRIER_STATUS_SYMBOL[g.carrierStatus]
  );
  if (!derivable.length) return '';
  const representative =
    derivable.find((g) => POSITIVE_STATUSES.has(g.carrierStatus)) ||
    derivable[0];
  return CARRIER_STATUS_SYMBOL[representative.carrierStatus];
}

// SIZE. The mark used to render at exactly the label font size, which is a thin
// tick beside a 155px symbol — clinicians reported it being easy to miss at a
// glance, especially zoomed out. It now renders at MARK_FONT_SCALE × the label
// font, so it still follows the Pedigree Settings font size (as before) but from
// a much larger base. Clamped to the symbol so the extremes of the two sliders
// (font 130 on a 100px symbol) can't produce a mark larger than the node itself,
// nor a mark that disappears at font 30 on a 200px one.
export const MARK_FONT_SCALE = 1.7;
// A multi-glyph mark is ~3× as wide as '+', so it renders a shade smaller to stay in
// proportion with the node instead of sprawling across the chart. Retained as a
// safeguard, but NO current carrier status produces a multi-glyph marker — the paired
// allele glyphs were removed in TGCLOUD-1647 — so this is currently unexercised.
export const MULTI_GLYPH_FONT_SCALE = 0.78;
const MIN_FONT_RATIO = 0.32; // × symbol size
const MAX_FONT_RATIO = 0.85; // × symbol size

// Conservative advance widths as a fraction of font size, for the only glyphs a
// mark (or the neighbouring proxy "P") can contain. Estimated rather than
// measured — cf. labelText's AVG_GLYPH_W — because the lane must resolve before
// anything is in the DOM, and a getBBox per node forces a synchronous reflow.
const GLYPH_EM = { '+': 0.62, '-': 0.4, '/': 0.34 };
const FALLBACK_GLYPH_EM = 0.62;
const PROXY_GLYPH_EM = 0.72; // bold "P"
export const ASCENT_EM = 0.75; // '/' is the tallest glyph a mark can contain

// WEIGHT. The mark renders at the REGULAR weight, on purpose: size alone carries
// it at a glance, and bold (tried first) read as heavy-handed against the light
// symbol outlines. Advance widths above are near-identical either way, so the
// lane geometry is unaffected by the choice.

// CLEARANCE, as a fraction of the symbol size. The mark hangs off the top-right
// corner on the 45° ray, starting beyond whatever else already reaches into that
// corner:
//   • the symbol — a female circle is the widest, r = S/√π ≈ 0.564·S;
//   • the deceased slash, tip at (0.62·S, −0.62·S) — Canvas DECEASED_SLASH;
//   • an adoption bracket — right stem and top stub both at 0.8·S.
// Everything else in the corner is text and is laid out by topRightLaneX below.
const SYMBOL_RADIUS_RATIO = 1 / Math.sqrt(Math.PI);
const DECEASED_SLASH_RATIO = 0.62;
const ADOPTION_BRACKET_RATIO = 0.8;
// Breathing room between the mark and both the node and its lane neighbours, so
// a bigger glyph doesn't crowd the symbol it annotates.
export const CORNER_GAP_RATIO = 0.09;

// The proxy "P" renders at this fraction of the label font (Canvas).
export const PROXY_LABEL_FONT_SCALE = 0.8;

const num = (v, fallback) =>
  Number.isFinite(Number(v)) ? Number(v) : fallback;

/** Estimated rendered width of `text` at `fontSize`. */
function markWidth(text, fontSize) {
  let em = 0;
  for (const ch of String(text)) em += GLYPH_EM[ch] ?? FALLBACK_GLYPH_EM;
  return em * fontSize;
}

/**
 * Font size for a mark: the label font scaled up, held inside the symbol-derived
 * bounds, and eased down again for multi-glyph marks.
 * @param {number} fontSize label font size (Pedigree Settings)
 * @param {number} symbolSize config.SYMBOL_SIZE
 * @param {string} text the mark glyphs
 * @returns {number}
 */
export function carrierStatusMarkFontSize(fontSize, symbolSize, text = '+') {
  const sym = num(symbolSize, config.SYMBOL_SIZE);
  const fs = num(fontSize, 70);
  const clamped = Math.min(
    Math.max(fs * MARK_FONT_SCALE, sym * MIN_FONT_RATIO),
    sym * MAX_FONT_RATIO
  );
  return String(text).length > 1 ? clamped * MULTI_GLYPH_FONT_SCALE : clamped;
}

/**
 * Where and how big to draw a node's carrier-status mark, or null when it has
 * none. Rendered with text-anchor:start on the baseline, so `x` is the ink's
 * left edge and `y` its bottom (these glyphs have no descender) — which puts the
 * whole mark clear of the node in BOTH axes, never overlapping the symbol.
 * @param {string} text from carrierStatusMarkText
 * @param {{ fontSize?: number, symbolSize?: number, deceased?: boolean,
 *           adopted?: boolean }} [opts]
 * @returns {{ text: string, fontSize: number, x: number, y: number,
 *             width: number, right: number, top: number }|null}
 */
export function carrierStatusMarkGeometry(text, opts = {}) {
  const glyphs = String(text || '');
  if (!glyphs) return null;

  const sym = num(opts.symbolSize, config.SYMBOL_SIZE);
  const fontSize = carrierStatusMarkFontSize(opts.fontSize, sym, glyphs);
  const clearance =
    sym *
    (Math.max(
      SYMBOL_RADIUS_RATIO,
      opts.deceased ? DECEASED_SLASH_RATIO : 0,
      opts.adopted ? ADOPTION_BRACKET_RATIO : 0
    ) +
      CORNER_GAP_RATIO);
  const width = markWidth(glyphs, fontSize);

  return {
    text: glyphs,
    fontSize,
    x: clearance,
    y: -clearance,
    width,
    right: clearance + width,
    top: -clearance - fontSize * ASCENT_EM
  };
}

/**
 * X positions for the tenants of a node's top-right annotation lane, ordered
 * outward from the node: carrier mark → proxy "P" → nomenclature person number.
 * Each starts clear of the one before it, so the enlarged mark can never sit on
 * top of a neighbour. Their y positions are unchanged (each keeps its own
 * height) and all three overlap vertically, so resolving x resolves the lane.
 * With no mark present every position is its historical default.
 * @param {{ mark?: object|null, hasProxyP?: boolean, fontSize?: number,
 *           symbolSize?: number }} [opts]
 * @returns {{ proxyX: number, numberX: number }}
 */
export function topRightLaneX(opts = {}) {
  const sym = num(opts.symbolSize, config.SYMBOL_SIZE);
  const fs = num(opts.fontSize, 70);
  const gap = sym * CORNER_GAP_RATIO;
  const mark = opts.mark || null;

  const proxyX = Math.max(sym * 0.7, mark ? mark.right + gap : 0);
  const numberBase = sym * (opts.hasProxyP ? 1.15 : 0.95);
  const precedingRight = opts.hasProxyP
    ? proxyX + fs * PROXY_LABEL_FONT_SCALE * PROXY_GLYPH_EM
    : mark
    ? mark.right
    : 0;

  return { proxyX, numberX: Math.max(numberBase, precedingRight + gap) };
}
